"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
const jsx_runtime_1 = require("react/jsx-runtime");
/**
 * Copyright 2025 Scott Bender (scott@scottbender.net)
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
const react_1 = require("react");
const reactstrap_1 = require("reactstrap");
const RecordingContext_1 = require("../contexts/RecordingContext");
const localStorage_1 = require("../utils/localStorage");
const services_1 = require("../services");
const isEmbedded = typeof window !== 'undefined' && window.location.href.includes('/admin/');
const prefix = isEmbedded ? '/plugins/canboat-visual-analyzer' : '';
const RecordingTab = () => {
    const [recordingStatus, setRecordingStatus] = (0, react_1.useState)({
        isRecording: false,
        messageCount: 0,
        fileSize: 0,
    });
    const [recordingFiles, setRecordingFiles] = (0, react_1.useState)([]);
    const [customFileName, setCustomFileName] = (0, react_1.useState)('');
    const [autoGenerateFileName, setAutoGenerateFileName] = (0, react_1.useState)(true);
    const [recordingFormat, setRecordingFormat] = (0, react_1.useState)(() => {
        // Load last selected format from localStorage, default to 'passthrough'
        return localStorage_1.recordingStorage.getFormat();
    });
    const [loading, setLoading] = (0, react_1.useState)(false);
    const [error, setError] = (0, react_1.useState)(null);
    // Use recording context for real-time updates
    const { state: recordingContextState, dispatch } = (0, RecordingContext_1.useRecording)();
    // Update local state when context state changes
    (0, react_1.useEffect)(() => {
        if (recordingContextState.lastUpdate) {
            setRecordingStatus((prevStatus) => {
                const newStatus = recordingContextState.status;
                // Handle errors from WebSocket events
                if (newStatus.error) {
                    setError(newStatus.error);
                }
                else {
                    setError(null); // Clear error when no error in new status
                }
                // Refresh file list when recording starts/stops
                if (newStatus.isRecording !== prevStatus.isRecording) {
                    setTimeout(() => loadRecordingFiles(), 100); // Small delay to ensure backend is ready
                }
                return newStatus;
            });
        }
    }, [recordingContextState.lastUpdate]);
    // Available recording formats based on Transform tab
    const recordingFormats = [
        { value: 'passthrough', label: 'Source Format' },
        { value: 'canboat-json', label: 'Canboat JSON' },
        { value: 'canboat-json-pretty', label: 'Canboat JSON (Pretty)' },
        { value: 'actisense', label: 'Actisense Serial Format' },
        { value: 'actisense-n2k-ascii', label: 'Actisense N2K ASCII' },
        { value: 'ikonvert', label: 'iKonvert Format' },
        { value: 'ydwg-full-raw', label: 'Yacht Devices RAW Format' },
        { value: 'ydwg-raw', label: 'Yacht Devices RAW Send Format' },
        { value: 'pcdin', label: 'PCDIN Format' },
        { value: 'mxpgn', label: 'MXPGN Format' },
        { value: 'candump1', label: 'Linux CAN utils (Angstrom)' },
        { value: 'candump2', label: 'Linux CAN utils (Debian)' },
        { value: 'candump3', label: 'Linux CAN utils (log format)' },
    ];
    // Load initial recording status and files on component mount
    (0, react_1.useEffect)(() => {
        loadRecordingStatus();
        loadRecordingFiles();
        // Set up periodic refresh for file list (less frequent since status updates come via WebSocket)
        const interval = setInterval(() => {
            loadRecordingFiles();
        }, 10000); // Refresh file list every 10 seconds
        return () => clearInterval(interval);
    }, []);
    const loadRecordingStatus = async () => {
        try {
            const response = await services_1.server.get({ type: 'recording' }, '/status');
            if (response.success) {
                const status = response.result;
                // Update both local state and context
                setRecordingStatus(status);
                dispatch({ type: 'SET_STATUS', payload: status });
            }
        }
        catch (err) {
            console.error('Failed to load recording status:', err);
        }
    };
    const loadRecordingFiles = async () => {
        try {
            const response = await services_1.server.get({ type: 'recording' }, '/files');
            if (response.success) {
                const files = response.results;
                setRecordingFiles(files);
            }
        }
        catch (err) {
            console.error('Failed to load recording files:', err);
        }
    };
    const handleFormatChange = (newFormat) => {
        setRecordingFormat(newFormat);
        // Save to localStorage to remember for next session
        localStorage_1.recordingStorage.setFormat(newFormat);
    };
    const startRecording = async () => {
        setLoading(true);
        setError(null);
        try {
            const fileName = autoGenerateFileName
                ? undefined
                : customFileName ||
                    `recording_${new Date().toISOString().replace(/[:.]/g, '-')}.${getFileExtension(recordingFormat)}`;
            console.log('Starting recording with fileName:', fileName, 'format:', recordingFormat);
            const response = await services_1.server.post({ type: 'recording', value: { fileName, format: recordingFormat } }, '/start');
            console.log('Recording response:', response);
            if (response.success) {
                const result = response.result;
                // Don't set local state here - it will be updated via WebSocket events
                // Refresh file list
                loadRecordingFiles();
            }
            else {
                const errorData = response.error;
                throw new Error(errorData || 'Failed to start recording');
            }
        }
        catch (err) {
            const errorMessage = err instanceof Error ? err.message : 'Failed to start recording';
            setError(errorMessage);
            setRecordingStatus((prev) => ({ ...prev, error: errorMessage }));
        }
        finally {
            setLoading(false);
        }
    };
    const stopRecording = async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await services_1.server.post({ type: 'recording' }, '/stop');
            if (response.success) {
                // Don't set local state here - it will be updated via WebSocket events
                // Refresh file list to show the completed recording
                loadRecordingFiles();
            }
            else {
                const errorData = response.error;
                throw new Error(errorData || 'Failed to stop recording');
            }
        }
        catch (err) {
            const errorMessage = err instanceof Error ? err.message : 'Failed to stop recording';
            setError(errorMessage);
        }
        finally {
            setLoading(false);
        }
    };
    const deleteFile = async (fileName) => {
        if (!confirm(`Are you sure you want to delete ${fileName}?`)) {
            return;
        }
        try {
            const response = await services_1.server.delete({ type: 'recording' }, `/files/${encodeURIComponent(fileName)}`);
            if (response.success) {
                loadRecordingFiles();
            }
            else {
                const errorData = await response.json();
                setError(errorData.error || 'Failed to delete file');
            }
        }
        catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to delete file');
        }
    };
    const deleteAllFiles = async () => {
        if (recordingFiles.length === 0) {
            return;
        }
        if (!confirm(`Are you sure you want to delete all ${recordingFiles.length} recording files? This action cannot be undone.`)) {
            return;
        }
        setLoading(true);
        try {
            // Delete all files in parallel
            const deletePromises = recordingFiles.map((file) => services_1.server.delete({ type: 'recording' }, `/files/${encodeURIComponent(file.name)}`));
            const results = await Promise.allSettled(deletePromises);
            // Check for any failures
            const failures = results.filter((result) => result.status === 'rejected');
            if (failures.length > 0) {
                setError(`Failed to delete ${failures.length} file(s)`);
            }
            else {
                // Check for HTTP errors
                const responses = results.filter((result) => result.status === 'fulfilled').map((result) => result.value);
                const httpErrors = responses.filter((response) => !response.success);
                if (httpErrors.length > 0) {
                    setError(`Failed to delete ${httpErrors.length} file(s)`);
                }
            }
            // Refresh file list regardless of partial failures
            loadRecordingFiles();
        }
        catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to delete files');
        }
        finally {
            setLoading(false);
        }
    };
    const downloadFile = (fileName) => {
        window.open(`${prefix}/api/recording/files/${encodeURIComponent(fileName)}/download`, '_blank');
    };
    const formatFileSize = (bytes) => {
        if (bytes === 0)
            return '0 B';
        const k = 1024;
        const sizes = ['B', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    };
    const formatDate = (dateString) => {
        return dateString ? new Date(dateString).toLocaleString() : '';
    };
    const getFileExtension = (format) => {
        switch (format) {
            case 'canboat-json':
            case 'canboat-json-pretty':
                return 'json';
            case 'actisense':
            case 'actisense-n2k-ascii':
                return 'n2k';
            case 'ikonvert':
                return 'iko';
            case 'ydwg-full-raw':
            case 'ydwg-raw':
                return 'ydwg';
            case 'pcdin':
                return 'pcd';
            case 'mxpgn':
                return 'mxp';
            case 'candump1':
            case 'candump2':
            case 'candump3':
            case 'passthrough':
                return 'log';
            default:
                return 'json';
        }
    };
    return ((0, jsx_runtime_1.jsx)("div", { className: "container-fluid mt-3", children: (0, jsx_runtime_1.jsxs)(reactstrap_1.Row, { children: [(0, jsx_runtime_1.jsx)(reactstrap_1.Col, { md: 6, children: (0, jsx_runtime_1.jsxs)(reactstrap_1.Card, { children: [(0, jsx_runtime_1.jsx)(reactstrap_1.CardHeader, { children: (0, jsx_runtime_1.jsx)("h5", { children: "Recording Control" }) }), (0, jsx_runtime_1.jsxs)(reactstrap_1.CardBody, { children: [error && ((0, jsx_runtime_1.jsxs)(reactstrap_1.Alert, { color: "danger", className: "mb-3", children: [(0, jsx_runtime_1.jsx)("strong", { children: "Error:" }), " ", error] })), (0, jsx_runtime_1.jsxs)("div", { className: "mb-3", children: [(0, jsx_runtime_1.jsx)(reactstrap_1.Badge, { color: recordingStatus.isRecording ? 'success' : 'secondary', className: "me-2", children: recordingStatus.isRecording ? '● Recording' : '○ Not Recording' }), recordingStatus.isRecording && recordingStatus.fileName && ((0, jsx_runtime_1.jsxs)("small", { className: "text-muted", children: ["File: ", recordingStatus.fileName, recordingStatus.format && ((0, jsx_runtime_1.jsxs)("span", { className: "ms-2", children: ["Format:", ' ', recordingFormats.find((f) => f.value === recordingStatus.format)?.label ||
                                                                recordingStatus.format] }))] }))] }), recordingStatus.isRecording && ((0, jsx_runtime_1.jsxs)("div", { className: "mb-3", children: [(0, jsx_runtime_1.jsxs)(reactstrap_1.Row, { children: [(0, jsx_runtime_1.jsxs)(reactstrap_1.Col, { sm: 6, children: [(0, jsx_runtime_1.jsx)("strong", { children: "Messages:" }), " ", recordingStatus.messageCount] }), (0, jsx_runtime_1.jsxs)(reactstrap_1.Col, { sm: 6, children: [(0, jsx_runtime_1.jsx)("strong", { children: "Size:" }), " ", formatFileSize(recordingStatus.fileSize)] })] }), recordingStatus.startTime && ((0, jsx_runtime_1.jsxs)("div", { className: "mt-2", children: [(0, jsx_runtime_1.jsx)("strong", { children: "Started:" }), " ", formatDate(recordingStatus.startTime)] }))] })), !recordingStatus.isRecording && ((0, jsx_runtime_1.jsxs)("div", { className: "mb-3", children: [(0, jsx_runtime_1.jsxs)(reactstrap_1.FormGroup, { children: [(0, jsx_runtime_1.jsx)(reactstrap_1.Label, { for: "recordingFormat", children: "Recording Format:" }), (0, jsx_runtime_1.jsx)(reactstrap_1.Input, { type: "select", id: "recordingFormat", value: recordingFormat, onChange: (e) => handleFormatChange(e.target.value), children: recordingFormats.map((format) => ((0, jsx_runtime_1.jsx)("option", { value: format.value, children: format.label }, format.value))) })] }), (0, jsx_runtime_1.jsx)(reactstrap_1.FormGroup, { check: true, children: (0, jsx_runtime_1.jsxs)(reactstrap_1.Label, { check: true, children: [(0, jsx_runtime_1.jsx)(reactstrap_1.Input, { type: "checkbox", checked: autoGenerateFileName, onChange: (e) => setAutoGenerateFileName(e.target.checked) }), "Auto-generate filename"] }) }), !autoGenerateFileName && ((0, jsx_runtime_1.jsxs)(reactstrap_1.FormGroup, { className: "mt-2", children: [(0, jsx_runtime_1.jsx)(reactstrap_1.Label, { for: "fileName", children: "Custom filename:" }), (0, jsx_runtime_1.jsx)(reactstrap_1.Input, { type: "text", id: "fileName", value: customFileName, onChange: (e) => setCustomFileName(e.target.value), placeholder: `recording_2025-01-01.${getFileExtension(recordingFormat)}` })] }))] })), (0, jsx_runtime_1.jsx)("div", { className: "d-grid gap-2", children: !recordingStatus.isRecording ? ((0, jsx_runtime_1.jsx)(reactstrap_1.Button, { color: "success", onClick: startRecording, disabled: loading, children: loading ? 'Starting...' : 'Start Recording' })) : ((0, jsx_runtime_1.jsx)(reactstrap_1.Button, { color: "danger", onClick: stopRecording, disabled: loading, children: loading ? 'Stopping...' : 'Stop Recording' })) })] })] }) }), (0, jsx_runtime_1.jsx)(reactstrap_1.Col, { md: 6, children: (0, jsx_runtime_1.jsxs)(reactstrap_1.Card, { children: [(0, jsx_runtime_1.jsxs)(reactstrap_1.CardHeader, { className: "d-flex justify-content-between align-items-center", children: [(0, jsx_runtime_1.jsx)("h5", { className: "mb-0", children: "Recorded Files" }), recordingFiles.length > 0 && ((0, jsx_runtime_1.jsx)(reactstrap_1.Button, { size: "sm", color: "outline-danger", onClick: deleteAllFiles, disabled: loading, children: loading ? 'Deleting...' : 'Delete All' }))] }), (0, jsx_runtime_1.jsx)(reactstrap_1.CardBody, { children: recordingFiles.length === 0 ? ((0, jsx_runtime_1.jsx)("p", { className: "text-muted", children: "No recordings found." })) : ((0, jsx_runtime_1.jsxs)(reactstrap_1.Table, { responsive: true, size: "sm", children: [(0, jsx_runtime_1.jsx)("thead", { children: (0, jsx_runtime_1.jsxs)("tr", { children: [(0, jsx_runtime_1.jsx)("th", { children: "Filename" }), (0, jsx_runtime_1.jsx)("th", { children: "Format" }), (0, jsx_runtime_1.jsx)("th", { children: "Size" }), (0, jsx_runtime_1.jsx)("th", { children: "Messages" }), (0, jsx_runtime_1.jsx)("th", { children: "Created" }), (0, jsx_runtime_1.jsx)("th", { children: "Actions" })] }) }), (0, jsx_runtime_1.jsx)("tbody", { children: recordingFiles.map((file) => ((0, jsx_runtime_1.jsxs)("tr", { children: [(0, jsx_runtime_1.jsx)("td", { children: (0, jsx_runtime_1.jsx)("small", { children: file.name }) }), (0, jsx_runtime_1.jsx)("td", { children: (0, jsx_runtime_1.jsx)("small", { children: file.format
                                                                ? recordingFormats.find((f) => f.value === file.format)?.label || file.format
                                                                : 'Unknown' }) }), (0, jsx_runtime_1.jsx)("td", { children: formatFileSize(file.size) }), (0, jsx_runtime_1.jsx)("td", { children: file.messageCount.toLocaleString() }), (0, jsx_runtime_1.jsx)("td", { children: (0, jsx_runtime_1.jsx)("small", { children: formatDate(file.created) }) }), (0, jsx_runtime_1.jsxs)("td", { children: [(0, jsx_runtime_1.jsx)(reactstrap_1.Button, { size: "sm", color: "primary", className: "me-1", onClick: () => downloadFile(file.name), children: "Download" }), (0, jsx_runtime_1.jsx)(reactstrap_1.Button, { size: "sm", color: "danger", onClick: () => deleteFile(file.name), children: "Delete" })] })] }, file.name))) })] })) })] }) })] }) }));
};
exports.default = RecordingTab;
